import { useEffect, useRef, useState } from 'react';

import { api } from '../api/index.ts';
import { ApiError, type Category, type Entry, type Favourite, type ItemPurchase } from '../api/types.ts';
import { useT } from '../i18n/index.ts';
import { karachiDate } from '../lib/dates.ts';
import { applyOrder, categoryList, loadOrder, saveOrder } from '../lib/entityOrder.ts';
import { recentFix, rememberFix, startLocating, type Locator } from '../lib/location.ts';
import { compressPhoto } from '../lib/photo.ts';
import { goWalletHome, navigate, useRoute } from '../lib/router.ts';
import { isUnit, sizeFromName, stepQuantity } from '../lib/units.ts';
import { uuid } from '../lib/uuid.ts';
import { EntityIcon, Icon } from '../ui/icons.tsx';
import { OfflinePill } from '../ui/parts.tsx';
import {
  amountHint,
  amountOf,
  canSave,
  emptyDraft,
  isLikelyDuplicate,
  isOverBalance,
  itemRowTarget,
  progress,
  reasonText,
  sameDayOf,
  saveHint,
  stepNumber,
  type Draft,
  type Step,
} from './draft.ts';
import { clearDraft, loadDraft, saveDraft } from './draftStore.ts';
import { Saved } from './Saved.tsx';
import { Slip } from './Slip.tsx';
import { AmountStep, Confirm, ItemStep, ProofStep, WalletStep, WhatStep } from './steps.tsx';
import { useOnline, useSpender } from './store.tsx';

type Overlay = 'wallet' | 'dup' | 'discard' | null;
/** How long Save waits for a GPS fix, and how old a remembered one may be. */
const LOCATION_WAIT_MS = 6_000;
const RECENT_FIX_MS = 15 * 60_000;
const STEPS: readonly Step[] = ['what', 'item', 'amount', 'proof'];

/** localStorage, or undefined where the browser blocks it (some private modes throw). */
function browserStorage(): Storage | undefined {
  try {
    return window.localStorage;
  } catch {
    return undefined;
  }
}

function draftFromEntry(e: Entry, today: string): Draft {
  return {
    ...emptyDraft(e.entityId, today),
    categoryId: e.categoryId,
    itemName: e.itemName,
    quantity: e.quantity ?? 0,
    unit: isUnit(e.unit) ? e.unit : null,
    amount: String(e.amount),
    txnDate: e.txnDate,
    correctsId: e.id,
    // Paid for by another house: it never came out of his cash.
    correctsAmount: e.paidByEntityId ? 0 : e.amount,
    paidBy: e.paidByEntityId ?? null,
  };
}

export function AddExpense() {
  const { t } = useT();
  const s = useSpender();
  const online = useOnline();
  const route = useRoute();
  const today = karachiDate();
  const fixId = route.query.get('fix');
  const fixEntry = fixId ? s.entries.find((e) => e.id === fixId) : undefined;

  // A refresh brings back what was typed (amount, item, …) until it is saved or abandoned.
  const [draft, setDraft] = useState<Draft>(() =>
    fixEntry
      ? draftFromEntry(fixEntry, today)
      : (loadDraft(browserStorage(), s.me.id, today, route.query.get('entity')) ??
        emptyDraft(route.query.get('entity') || s.lastEntityId, today)),
  );
  const [overlay, setOverlay] = useState<Overlay>(null);
  const [flash, setFlash] = useState(false);
  const [preparing, setPreparing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<{ entry: Entry; balance: number; draft: Draft } | null>(null);

  // Keep the form in localStorage as it is filled in (not while the Saved screen shows).
  useEffect(() => {
    if (!saved) saveDraft(browserStorage(), s.me.id, draft);
  }, [draft, saved, s.me.id]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [favourites, setFavourites] = useState<Favourite[]>([]);
  const [suggestions, setSuggestions] = useState<ItemPurchase[]>([]);
  const [lastBought, setLastBought] = useState<ItemPurchase | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  // The phone's photos (no camera): a receipt photographed earlier.
  const galleryInput = useRef<HTMLInputElement>(null);
  // Location: tracked while this screen is open (once, not on every render), stopped on leave.
  const locator = useRef<Locator | null>(null);
  useEffect(() => {
    locator.current = startLocating();
    return () => locator.current?.stop();
  }, []);

  const entity = s.entityById(draft.entityId) ?? s.entities[0];
  const balance = s.balanceOf(draft.entityId);
  const category = categories.find((c) => c.id === draft.categoryId);
  const patch = (p: Partial<Draft>) => setDraft((d) => ({ ...d, ...p }));

  // Step lives in the URL so the phone's back button steps back through the form.
  const wanted = route.query.get('step') as Step | null;
  const p = progress(draft, balance);
  const step: Step =
    wanted &&
    STEPS.includes(wanted) &&
    !(wanted === 'item' && !draft.categoryId) &&
    !(wanted === 'amount' && !p.itemDone) &&
    !(wanted === 'proof' && (!p.itemDone || !p.quantityDone || amountOf(draft) < 1))
      ? wanted
      : fixEntry
        ? 'proof'
        : 'what';
  const goStep = (next: Step) => {
    setOverlay(null);
    const q = new URLSearchParams({ entity: draft.entityId, step: next });
    if (fixId) q.set('fix', fixId);
    navigate(`/add?${q.toString()}`);
  };

  // Categories and usual buys for the chosen wallet; most-used categories first.
  useEffect(() => {
    let live = true;
    void Promise.all([api.categories(draft.entityId), api.favourites(draft.entityId)]).then(([cats, favs]) => {
      if (!live) return;
      const uses = new Map<string, number>();
      for (const e of s.entries) uses.set(e.categoryId, (uses.get(e.categoryId) ?? 0) + 1);
      const byUse = [...cats].sort((a, b) => (uses.get(b.id) ?? 0) - (uses.get(a.id) ?? 0) || a.sortOrder - b.sortOrder);
      // The spender's own arrangement (hold and drag a tile) wins over most-used.
      const custom = loadOrder(browserStorage(), s.me.id, categoryList(draft.entityId));
      setCategories(custom.length ? applyOrder(byUse, custom) : byUse);
      setFavourites(favs);
    });
    return () => {
      live = false;
    };
    // Deliberately keyed on the wallet only: re-sorting on every new entry would move tiles
    // under the spender's thumb.
  }, [draft.entityId]);


  useEffect(() => {
    if (!draft.categoryId) return;
    let live = true;
    void api.recentItems(draft.entityId, draft.categoryId).then((items) => live && setSuggestions(items));
    return () => {
      live = false;
    };
  }, [draft.entityId, draft.categoryId]);

  // "Last bought" for the chosen item (SPEC §5), looked up after typing settles.
  useEffect(() => {
    if (!draft.categoryId || draft.itemName.trim().length < 2) {
      setLastBought(null);
      return;
    }
    let live = true;
    const id = window.setTimeout(() => {
      void api
        .lastBought(draft.entityId, draft.categoryId ?? '', draft.itemName)
        .then((hit) => live && setLastBought(hit))
        .catch(() => live && setLastBought(null));
    }, 250);
    return () => {
      live = false;
      window.clearTimeout(id);
    };
  }, [draft.entityId, draft.categoryId, draft.itemName]);

  useEffect(() => {
    if (!flash) return;
    const id = window.setTimeout(() => setFlash(false), 900);
    return () => window.clearTimeout(id);
  }, [flash]);

  // Free photo previews when leaving.
  const photosRef = useRef(draft.photos);
  photosRef.current = draft.photos;
  useEffect(() => () => photosRef.current.forEach((ph) => URL.revokeObjectURL(ph.url)), []);

  const hasData = Boolean(draft.categoryId || draft.amount || draft.photos.length || draft.noReceipt);
  const over = isOverBalance(draft, balance);
  const hint = saveHint(draft, balance);
  const savable = canSave(draft, balance);

  async function onPhotoChosen(file: File | undefined) {
    if (!file) return;
    setPreparing(true);
    try {
      const blob = await compressPhoto(file);
      const ph = { id: uuid(), blob, url: URL.createObjectURL(blob) };
      setDraft((d) => ({ ...d, photos: [...d.photos, ph].slice(0, 3), noReceipt: false }));
    } finally {
      setPreparing(false);
    }
  }

  async function save(skipDuplicateCheck = false) {
    if (!savable || saving) return;
    if (!skipDuplicateCheck && isLikelyDuplicate(draft, s.recentSaves, Date.now())) {
      setOverlay('dup');
      return;
    }
    setOverlay(null);
    setSaving(true);
    setError(null);
    // Where it was bought: a fix if one comes in time, else one from the last minutes (e.g. the
    // check before Home). Location is optional (owner's decision, 11 Oct 2026): without one the
    // entry still saves, without a place, so the wallet always works.
    const location = (await locator.current?.settle(LOCATION_WAIT_MS)) ?? recentFix(RECENT_FIX_MS);
    if (location) rememberFix(location);
    try {
      const result = await api.saveExpense({
        clientRef: uuid(),
        entityId: draft.entityId,
        categoryId: draft.categoryId ?? '',
        itemName: draft.itemName.trim(),
        quantity: draft.quantity > 0 ? draft.quantity : null,
        unit: draft.quantity > 0 ? draft.unit : null,
        amount: amountOf(draft),
        txnDate: draft.txnDate,
        noReceiptReason: reasonText(draft, { shop: t('add.reasonShop'), street: t('add.reasonStreet'), lost: t('add.reasonLost') }),
        photos: draft.photos.map((ph) => ph.blob),
        location,
        correctsId: draft.correctsId,
      });
      navigator.vibrate?.([12, 60, 12]);
      s.rememberSave({ entityId: draft.entityId, categoryId: draft.categoryId ?? '', amount: amountOf(draft), savedAt: Date.now() });
      s.setLastEntity(draft.entityId);
      clearDraft(browserStorage(), s.me.id); // saved: nothing to bring back on refresh
      setSaved({ ...result, draft });
      void s.refresh();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : t('app.error'));
    } finally {
      setSaving(false);
    }
  }

  async function undo() {
    if (!saved) return;
    await api.deleteEntry(saved.entry.id, saved.draft.entityId);
    setDraft(saved.draft);
    setSaved(null);
    await s.refresh();
    goStep('proof');
  }

  function another() {
    const id = saved?.draft.entityId ?? draft.entityId;
    setSaved(null);
    setDraft(emptyDraft(id, today));
    navigate(`/add?entity=${id}`, { replace: true });
  }

  if (saved && entity) {
    return (
      <Saved
        entry={saved.entry}
        entity={s.entityById(saved.draft.entityId) ?? entity}
        balance={saved.balance}
        canUndo={!saved.draft.correctsId && saved.entry.status === 'pending'}
        payerName={saved.entry.paidByEntityId ? (s.entityById(saved.entry.paidByEntityId)?.name ?? null) : null}
        onAnother={another}
        onUndo={() => void undo()}
        onDone={goWalletHome}
      />
    );
  }
  if (!entity) return null;

  const title =
    overlay === 'wallet'
      ? t('add.whoseMoney')
      : overlay === 'dup'
        ? t('add.dupTitle')
        : overlay === 'discard'
          ? t('add.discardTitle')
          : { what: t('add.whatTitle'), item: t('add.itemTitle'), amount: t('add.amountTitle'), proof: t('add.proofTitle') }[step];

  return (
    <div className="flex h-app flex-col">
      {/* Phones: a little room above the bar so it does not touch the top of the screen. */}
      <header className="shrink-0 border-b-[1.5px] border-line bg-paper pt-[calc(env(safe-area-inset-top)+0.5rem)] pb-1.5 split:pt-[env(safe-area-inset-top)] split:pb-0">
        <div className="flex h-[60px] w-full items-center gap-1.5 ps-1 pe-3 split:px-4 low:h-12 kb:h-12 short:h-12 lg:px-10">
          <button
            type="button"
            aria-label={t('add.backHome')}
            onClick={() => (hasData && overlay !== 'discard' ? setOverlay('discard') : goWalletHome())}
            // A visible round button with a border (owner's request, 22 Oct 2026). A cross, not
            // an arrow: it closes the form, while the arrow beside the step title goes back a step.
            className="ms-1 flex size-11 shrink-0 items-center justify-center rounded-full border-2 border-line-strong bg-paper text-ink hover:bg-ground active:scale-95 focus-visible:outline-3 focus-visible:outline-offset-2 focus-visible:outline-ink"
          >
            {/* Leaves the form for Home (asking first if anything was filled in). */}
            <Icon name="close" size={22} stroke={2.75} />
          </button>
          <button
            type="button"
            aria-label={t('add.whoseMoney')}
            aria-expanded={overlay === 'wallet'}
            onClick={() => setOverlay(overlay === 'wallet' ? null : 'wallet')}
            className="flex h-[46px] min-w-0 flex-1 items-center gap-2 rounded-full ps-3.5 pe-3 text-base font-bold text-white hover:brightness-110 low:h-10 kb:h-10 split:max-w-md"
            style={{ background: entity.colour }}
          >
            <EntityIcon entity={entity} size={18} />
            <span className="min-w-0 flex-1 truncate text-start">{entity.name}</span>
            {/* Balance is also on the slip; very narrow phones drop it here to keep the name. */}
            <span className="num text-[15px] font-semibold max-[359px]:hidden">Rs {balance.toLocaleString('en-US')}</span>
            <Icon name="chevronDown" size={16} stroke={2.5} />
          </button>
          {!online && <OfflinePill compact />}
        </div>
      </header>

      {/* Phones: slip above the deck. Tablets, desktops and sideways phones: slip column
          beside the deck, each scrolling on its own. */}
      <div className="flex min-h-0 flex-1 flex-col split:w-full split:flex-row split:gap-5 split:px-4 split:py-4 short:gap-3 short:py-2 lg:gap-8 lg:px-10 lg:py-6">
        {/* While the phone keyboard is open the slip steps aside, so the field being typed in and
            the button under it stay above the keyboard. */}
        <aside
          className={`flex shrink-0 flex-col split:w-[min(42%,420px)] split:overflow-y-auto kb:hidden ${
            // On the first step nothing is filled in yet (the header shows the balance): phones skip it.
            !overlay && step === 'what' && !fixEntry ? 'low:hidden split:flex' : ''
          }`}
        >
          {fixEntry?.reviewNote && (
            <div className="flex shrink-0 items-start gap-2.5 border-b-[1.5px] border-warning bg-warning-tint px-4 py-2.5 text-[15px] leading-snug font-semibold text-warning-text split:mb-3 split:rounded-xl split:border-[1.5px]">
              <Icon name="pencil" size={18} className="mt-0.5" />
              <span>
                {t('add.ownerSays')}: {fixEntry.reviewNote}
              </span>
            </div>
          )}

          <Slip
            draft={draft}
            entity={entity}
            balance={balance}
            category={category}
            step={overlay ? null : step}
            flash={flash}
            lastBought={lastBought}
            payerName={draft.paidBy ? (s.entityById(draft.paidBy)?.name ?? null) : null}
            onRow={(row) => {
              if (row === 'item') goStep(itemRowTarget(draft));
              else if (row === 'amount') goStep(p.itemDone ? 'amount' : itemRowTarget(draft));
              else if (p.itemDone && amountOf(draft) > 0) goStep('proof');
            }}
          />
        </aside>

        <section className="@container safe-bottom flex min-h-0 flex-1 flex-col gap-3 px-4 pt-3.5 low:gap-2 low:pt-2.5 kb:gap-2 kb:pt-2 split:rounded-2xl split:border-[1.5px] split:border-line split:bg-paper split:p-5 short:gap-2 short:p-3 lg:p-6">
          {/* Tablets and desktops show a warning's title inside its centred card instead. */}
          <div
            className={`flex min-h-10 shrink-0 items-center gap-2.5 ${overlay === 'dup' || overlay === 'discard' ? 'split:hidden' : ''}`}
          >
            {!overlay && step !== 'what' && (
              <button
                type="button"
                aria-label={t('add.back')}
                onClick={() => window.history.back()}
                // Solid and dark so it is easy to find (owner's request, 22 Oct 2026).
                className="flex size-12 shrink-0 items-center justify-center rounded-full bg-ink text-white shadow-[0_2px_6px_rgba(19,33,31,0.25)] hover:bg-ink-soft active:scale-95 focus-visible:outline-3 focus-visible:outline-offset-2 focus-visible:outline-ink low:size-11"
              >
                <Icon name="arrowStart" size={24} stroke={2.75} flip />
              </button>
            )}
            <h2 className="m-0 min-w-0 flex-1 text-fluid-title leading-snug font-bold">{title}</h2>
            {!overlay && (
              <span className="num shrink-0 text-[13px] font-bold whitespace-nowrap text-muted">
                {t('add.step', { n: stepNumber(step) })}
              </span>
            )}
          </div>

          {error && (
            <div
              role="alert"
              className="flex shrink-0 items-center gap-2 rounded-xl bg-danger-tint px-3.5 py-2.5 text-[15px] font-semibold text-danger-text"
            >
              <Icon name="alert" size={18} />
              {error}
            </div>
          )}

          {overlay === 'wallet' && (
            <WalletStep
              entities={s.entities}
              wallets={s.wallets}
              currentId={draft.entityId}
              onPick={(id) => {
                // Categories are per entity, so a new wallet starts the item over.
                if (id !== draft.entityId)
                  setDraft((d) => ({ ...emptyDraft(id, today), amount: d.amount, photos: d.photos, txnDate: d.txnDate }));
                setOverlay(null);
                navigate(`/add?entity=${id}`, { replace: true });
              }}
            />
          )}
          {overlay === 'dup' && (
            <Confirm
              tone="warning"
              title={title}
              body={t('add.dupBody')}
              primary={t('add.dupBack')}
              secondary={t('add.dupSave')}
              onPrimary={() => setOverlay(null)}
              onSecondary={() => void save(true)}
            />
          )}
          {overlay === 'discard' && (
            <Confirm
              tone="danger"
              title={title}
              body={t('add.discardBody')}
              primary={t('add.keepFilling')}
              secondary={t('add.leave')}
              onPrimary={() => setOverlay(null)}
              onSecondary={() => {
                clearDraft(browserStorage(), s.me.id); // "Leave" means throw the draft away
                goWalletHome();
              }}
            />
          )}

          {!overlay && step === 'what' && (
            <WhatStep
              entity={entity}
              favourites={favourites}
              categories={categories}
              selectedCategoryId={draft.categoryId}
              onFavourite={(f) => {
                // One tap fills everything: the size comes from the name ("Milk 2 L") and the
                // usual price. The How much step then opens with them filled in, so the quantity,
                // unit and amount can still be changed (owner's request, 22 Oct 2026).
                const size = sizeFromName(f.name);
                setDraft((d) => ({
                  ...d,
                  categoryId: f.categoryId,
                  itemName: f.name,
                  quantity: size?.quantity ?? 0,
                  unit: size?.unit ?? null,
                  amount: String(f.defaultPrice),
                }));
                setFlash(true);
                goStep('amount');
              }}
              onCategory={(c) => {
                patch({ categoryId: c.id, itemName: '', quantity: 0 });
                goStep('item');
              }}
              onReorder={(ids) => {
                setCategories((cats) => applyOrder(cats, ids));
                saveOrder(browserStorage(), s.me.id, ids, categoryList(draft.entityId));
              }}
            />
          )}
          {!overlay && step === 'item' && (
            <ItemStep
              suggestions={suggestions}
              today={today}
              onPick={(it) => {
                // Quantity and unit start empty: what was bought this time is entered each time.
                patch({ itemName: it.itemName, quantity: 0, unit: null });
                goStep('amount');
              }}
              onNew={(name) => {
                patch({ itemName: name, quantity: 0, unit: null });
                goStep('amount');
              }}
            />
          )}
          {!overlay && step === 'amount' && (
            <AmountStep
              draft={draft}
              over={over}
              canNext={p.itemDone && p.quantityDone && p.amountDone}
              hint={(() => {
                const h = amountHint(draft);
                return h ? t(`add.${h}`) : null;
              })()}
              lastPrice={lastBought?.amount ?? null}
              onAmount={(digits) => patch({ amount: digits })}
              onQuantity={(dir) => setDraft((d) => ({ ...d, quantity: stepQuantity(d.quantity, d.unit, dir) }))}
              onQuantityTyped={(q) => patch({ quantity: q })}
              onUnit={(u) => patch({ unit: u })}
              onNext={() => goStep('proof')}
            />
          )}
          {!overlay && step === 'proof' && (
            <ProofStep
              draft={draft}
              today={today}
              preparing={preparing}
              saving={saving}
              canSave={savable}
              hint={hint ? t(`add.${hint}`) : null}
              onTakePhoto={() => fileInput.current?.click()}
              onPickPhoto={() => galleryInput.current?.click()}
              onRemovePhoto={(id) =>
                setDraft((d) => {
                  const gone = d.photos.find((ph) => ph.id === id);
                  if (gone) URL.revokeObjectURL(gone.url);
                  return { ...d, photos: d.photos.filter((ph) => ph.id !== id) };
                })
              }
              onNoReceipt={(on) => patch({ noReceipt: on })}
              onReason={(r) => patch({ reason: r })}
              onReasonText={(text) => patch({ reasonText: text })}
              onDate={(iso) => patch({ txnDate: iso })}
              onSave={() => void save()}
              sameDay={sameDayOf(s.entries, draft)}
            />
          )}
        </section>
      </div>

      {/* Opens the rear camera directly on phones (SPEC §5). */}
      <input
        ref={fileInput}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={(e) => {
          void onPhotoChosen(e.target.files?.[0]);
          e.target.value = '';
        }}
      />
      {/* The gallery: the same, without capture, so the phone offers its photos. */}
      <input
        ref={galleryInput}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => {
          void onPhotoChosen(e.target.files?.[0]);
          e.target.value = '';
        }}
      />
    </div>
  );
}
